import { CClient, showErrorAlert } from '@common/utils'
import { CANCEL_REASONS } from '@lib/enums/dashboard'
import { flattenErrorsDictFromResponse } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import { Dispatch, SetStateAction } from 'react'
import date from '@lib/utils/date.utils'
import { t } from '@lingui/macro'

type HandleRescheduleAtSelectedTimeOptionsType = {
  setInlineError: Dispatch<SetStateAction<string>>
  selectedDate: string
  selectedTime: string
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
  classroomId: number | string
  onSuccess?: () => void
  isStudent?: boolean
  on404?: () => void
  isTeacher?: boolean
}

/** i18n support added */
export const handleReschdeuleAtSelectedTime = async ({
  setInlineError,
  selectedDate,
  selectedTime,
  isLoading,
  setIsLoading,
  classroomId,
  onSuccess,
  isStudent, // isStudent can also be true when a teacher reschedules a class from the teacher dashboard at the student's request.
  on404,
  isTeacher = false, // Will be true when triggered from teacher dashboard
}: HandleRescheduleAtSelectedTimeOptionsType) => {
  if (isLoading) return
  setInlineError('')
  if (!selectedDate || !selectedTime) {
    setInlineError('Please select both date and time for booking the class.')
    return
  }
  setIsLoading(true)
  try {
    const response = await CClient(
      `/api/v1/classrooms/${classroomId}/reschedule/`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },

        // TODO: Figure out how to send slotEventId of selected slot in payload
        body: JSON.stringify({
          start: selectedTime,
          cancelReason: isStudent
            ? CANCEL_REASONS.CANCEL_REQUEST_BY_STUDENT
            : CANCEL_REASONS.CANCEL_REQUEST_BY_TEACHER,
          cancelReasonOther: isTeacher
            ? 'rescheduled-from-teacher-dashboard'
            : '',
        }),
      }
    )
    if (response.ok) onSuccess?.()
    else if (response.status === 404) on404?.()
    else {
      let errorMessage = await flattenErrorsDictFromResponse(response)
      // Remove "request: " prefix if present
      if (errorMessage && errorMessage.startsWith('request: ')) {
        errorMessage = errorMessage.replace('request: ', '')
      }
      throw new Error(errorMessage || 'Reschedule failed')
    }
  } catch (error) {
    captureException(error)

    // Extract error message safely
    const errorMsg = error instanceof Error ? error.message : String(error)

    // Determine what message to show
    let displayMessage = t({
      id: 'trialClass.rescheduleModal.errorMessage.generic',
      message:
        'Something went wrong while rescheduling your class. Please refresh the page and try again!',
    })

    // For teachers, always show the actual error
    if (isTeacher && errorMsg && errorMsg !== 'Reschedule failed') {
      displayMessage = errorMsg
    } else if (errorMsg && errorMsg !== 'Reschedule failed') {
      // For students, show user-friendly messages based on error type
      const lowerError = errorMsg.toLowerCase()

      if (
        lowerError.includes('no longer available') ||
        lowerError.includes('not available')
      ) {
        displayMessage = t({
          id: 'trialClass.rescheduleModal.errorMessage.slotUnavailable',
          message:
            'This time slot is no longer available. Please select a different time slot.',
        })
      } else if (
        lowerError.includes('group batch') ||
        lowerError.includes('group class')
      ) {
        displayMessage = t({
          id: 'trialClass.rescheduleModal.errorMessage.groupClass',
          message:
            'Group class reschedules require assistance. Please contact our support team.',
        })
      } else if (
        lowerError.includes('week off') ||
        lowerError.includes('week_off')
      ) {
        displayMessage = t({
          id: 'trialClass.rescheduleModal.errorMessage.weekOff',
          message:
            "This time slot conflicts with your teacher's schedule. Please choose a different day or time.",
        })
      } else if (lowerError.includes('collision')) {
        displayMessage = t({
          id: 'trialClass.rescheduleModal.errorMessage.collision',
          message:
            'This time slot is not available. Please select a different time.',
        })
      } else if (lowerError.match(/^\d{3}$/)) {
        // Just a status code, use generic message
        displayMessage = t({
          id: 'trialClass.rescheduleModal.errorMessage.generic',
          message:
            'Something went wrong while rescheduling your class. Please refresh the page and try again!',
        })
      } else {
        // Show specific error message for other cases
        displayMessage = errorMsg
      }
    }

    showErrorAlert({
      text: displayMessage,
    })
  }
  setIsLoading(false)
}

export const checkCanStudentRescheduleTrialClass = (startTime: string) =>
  date().isSameOrBefore(date(startTime).subtract(15, 'minutes'))
